import { Package } from "lucide-react";
import CrudManager, { slugify } from "@/components/admin/CrudManager";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/admin/ImageUpload";
import { ADMIN } from "@/constants/testIds";

const DEFAULT = {
  name: "", slug: "", brand: "", category: "", gender: "", description: "",
  features: [], sizes: [], colors: [], moq: 30, images: [], tags: [],
  featured: false, trending: false, new_arrival: false, published: true,
};

export default function AdminProducts() {
  return (
    <CrudManager
      title="Products"
      subtitle="Manage the wholesale catalogue. Products marked 'Featured' appear on the homepage."
      endpoint="/admin/products"
      defaultItem={DEFAULT}
      testId={ADMIN.productsList}
      searchKeys={["name", "brand", "category"]}
      listRender={(p) => (
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-sm bg-secondary overflow-hidden shrink-0">
            {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-foreground/30"><Package size={16} /></div>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[0.65rem] tracking-widest uppercase text-gold">{p.brand || "—"} • {p.category || "—"}</p>
            <p className="mt-0.5 font-serif text-lg text-foreground truncate">{p.name}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span className="text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-secondary text-foreground/70">MOQ {p.moq}</span>
              {p.featured && <span className="text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-gold/20 text-gold">Featured</span>}
              {p.new_arrival && <span className="text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-navy/30 text-navy">New</span>}
              {p.trending && <span className="text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-emerald_brand/20 text-emerald-500">Trending</span>}
              {!p.published && <span className="text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-destructive/20 text-destructive">Draft</span>}
            </div>
          </div>
        </div>
      )}
      formRenderer={(v, patch) => (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Product Name</Label>
              <Input className="mt-2 rounded-sm" value={v.name} onChange={(e) => patch({ name: e.target.value, slug: v.slug || slugify(e.target.value) })} />
            </div>
            <div>
              <Label>Slug</Label>
              <Input className="mt-2 rounded-sm" value={v.slug} onChange={(e) => patch({ slug: slugify(e.target.value) })} />
            </div>
            <div>
              <Label>Brand</Label>
              <Input className="mt-2 rounded-sm" value={v.brand} onChange={(e) => patch({ brand: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <Input className="mt-2 rounded-sm" value={v.category} onChange={(e) => patch({ category: e.target.value })} />
            </div>
            <div>
              <Label>Gender (Men/Women/Kids/Unisex)</Label>
              <Input className="mt-2 rounded-sm" value={v.gender} onChange={(e) => patch({ gender: e.target.value })} />
            </div>
            <div>
              <Label>MOQ (pieces)</Label>
              <Input type="number" className="mt-2 rounded-sm" value={v.moq} onChange={(e) => patch({ moq: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea className="mt-2 rounded-sm min-h-[90px]" value={v.description} onChange={(e) => patch({ description: e.target.value })} />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Sizes (comma separated)</Label>
              <Input className="mt-2 rounded-sm" value={(v.sizes || []).join(", ")} onChange={(e) => patch({ sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div>
              <Label>Colors (comma separated)</Label>
              <Input className="mt-2 rounded-sm" value={(v.colors || []).join(", ")} onChange={(e) => patch({ colors: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div>
              <Label>Features (comma separated)</Label>
              <Input className="mt-2 rounded-sm" value={(v.features || []).join(", ")} onChange={(e) => patch({ features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
          </div>
          <ImageUpload label="Primary Image" value={v.images?.[0] || ""} onChange={(url) => patch({ images: url ? [url, ...(v.images || []).slice(1)] : (v.images || []).slice(1) })} />
          <ImageUpload label="Secondary Image (Optional)" value={v.images?.[1] || ""} onChange={(url) => { const arr = [...(v.images || [])]; if (url) arr[1] = url; else arr.splice(1, 1); patch({ images: arr }); }} />
          <div className="grid md:grid-cols-4 gap-4 pt-2">
            <label className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/40 cursor-pointer">
              <Switch checked={v.published} onCheckedChange={(c) => patch({ published: c })} />
              <span className="text-sm">Published</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/40 cursor-pointer">
              <Switch checked={v.featured} onCheckedChange={(c) => patch({ featured: c })} />
              <span className="text-sm">Featured</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/40 cursor-pointer">
              <Switch checked={v.trending} onCheckedChange={(c) => patch({ trending: c })} />
              <span className="text-sm">Trending</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-sm border border-border/60 bg-secondary/40 cursor-pointer">
              <Switch checked={v.new_arrival} onCheckedChange={(c) => patch({ new_arrival: c })} />
              <span className="text-sm">New Arrival</span>
            </label>
          </div>
        </>
      )}
    />
  );
}
